import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Video, 
  Image, 
  Code, 
  Pencil, 
  Zap, 
  Headphones, 
  Users, 
  Palette, 
  Search, 
  Mic, 
  BarChart3, 
  Settings, 
  Building2, 
  FolderKanban,
  ChevronRight,
  Sparkles,
  Shield,
  Cpu,
  Megaphone,
  Cloud
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

const API_URL = import.meta.env.VITE_API_URL || '';

// Map category names to icons
const categoryIcons = {
  "Video Generation": Video,
  "Image Generation": Image,
  "Coding Assistants": Code,
  "Writing & Content": Pencil,
  "Productivity": Zap,
  "Customer Support": Headphones,
  "Meeting & Transcription": Users,
  "Design & Creative": Palette,
  "Research & Search": Search,
  "Voice & Audio": Mic,
  "Data & Analytics": BarChart3,
  "Developer Tools": Settings,
  "Enterprise & Automation": Building2,
  "Project Management": FolderKanban,
  "Security & Identity": Shield,
  "IoT & Hardware": Cpu,
  "Marketing & Sales": Megaphone,
  "Cloud & Infrastructure": Cloud,
};

// Map category names to gradient colors
const categoryColors = {
  "Video Generation": "from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20",
  "Image Generation": "from-blue-500/10 to-cyan-500/10 hover:from-blue-500/20 hover:to-cyan-500/20",
  "Coding Assistants": "from-green-500/10 to-emerald-500/10 hover:from-green-500/20 hover:to-emerald-500/20",
  "Writing & Content": "from-orange-500/10 to-amber-500/10 hover:from-orange-500/20 hover:to-amber-500/20",
  "Productivity": "from-yellow-500/10 to-lime-500/10 hover:from-yellow-500/20 hover:to-lime-500/20",
  "Customer Support": "from-teal-500/10 to-cyan-500/10 hover:from-teal-500/20 hover:to-cyan-500/20",
  "Meeting & Transcription": "from-indigo-500/10 to-blue-500/10 hover:from-indigo-500/20 hover:to-blue-500/20",
  "Design & Creative": "from-pink-500/10 to-rose-500/10 hover:from-pink-500/20 hover:to-rose-500/20",
  "Research & Search": "from-violet-500/10 to-purple-500/10 hover:from-violet-500/20 hover:to-purple-500/20",
  "Voice & Audio": "from-red-500/10 to-orange-500/10 hover:from-red-500/20 hover:to-orange-500/20",
  "Data & Analytics": "from-sky-500/10 to-blue-500/10 hover:from-sky-500/20 hover:to-blue-500/20",
  "Developer Tools": "from-slate-500/10 to-gray-500/10 hover:from-slate-500/20 hover:to-gray-500/20",
  "Enterprise & Automation": "from-emerald-500/10 to-teal-500/10 hover:from-emerald-500/20 hover:to-teal-500/20",
  "Project Management": "from-amber-500/10 to-yellow-500/10 hover:from-amber-500/20 hover:to-yellow-500/20",
  "Security & Identity": "from-red-500/10 to-rose-500/10 hover:from-red-500/20 hover:to-rose-500/20",
  "IoT & Hardware": "from-cyan-500/10 to-teal-500/10 hover:from-cyan-500/20 hover:to-teal-500/20",
  "Marketing & Sales": "from-fuchsia-500/10 to-pink-500/10 hover:from-fuchsia-500/20 hover:to-pink-500/20",
  "Cloud & Infrastructure": "from-blue-500/10 to-indigo-500/10 hover:from-blue-500/20 hover:to-indigo-500/20",
};

const iconColors = {
  "Video Generation": "text-purple-600",
  "Image Generation": "text-blue-600",
  "Coding Assistants": "text-green-600",
  "Writing & Content": "text-orange-600",
  "Productivity": "text-yellow-600",
  "Customer Support": "text-teal-600",
  "Meeting & Transcription": "text-indigo-600",
  "Design & Creative": "text-pink-600",
  "Research & Search": "text-violet-600",
  "Voice & Audio": "text-red-600",
  "Data & Analytics": "text-sky-600",
  "Developer Tools": "text-slate-600",
  "Enterprise & Automation": "text-emerald-600",
  "Project Management": "text-amber-600",
  "Security & Identity": "text-red-600",
  "IoT & Hardware": "text-cyan-600",
  "Marketing & Sales": "text-fuchsia-600",
  "Cloud & Infrastructure": "text-blue-600",
};

export const CategoryBrowser = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/platforms/categories/popular`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (categoryName) => {
    navigate(`/browse/${encodeURIComponent(categoryName)}`);
  };

  const displayedCategories = showAll ? categories : categories.slice(0, 8);

  if (loading) {
    return (
      <section className="w-full py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <Skeleton className="h-8 w-64 mx-auto mb-2" />
            <Skeleton className="h-4 w-96 mx-auto" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) return null;

  return (
    <section className="w-full py-12 sm:py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 mb-3">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-2">
            Browse by Category
          </h2>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            Explore our curated AI platforms organized by what they do best
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
          {displayedCategories.map((category) => {
            const Icon = categoryIcons[category.name] || Sparkles;
            const gradientClass = categoryColors[category.name] || "from-gray-500/10 to-slate-500/10";
            const iconColor = iconColors[category.name] || "text-gray-600";
            
            return (
              <Card
                key={category.name}
                className={`cursor-pointer transition-all duration-200 border-0 bg-gradient-to-br ${gradientClass}`}
                onClick={() => handleCategoryClick(category.name)}
              >
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <div className={`w-10 h-10 rounded-lg bg-white/50 dark:bg-white/10 flex items-center justify-center mb-2`}>
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                  </div>
                  <span className="text-sm font-medium text-foreground line-clamp-1">
                    {category.name}
                  </span>
                  <Badge variant="secondary" className="mt-1.5 text-xs px-2 py-0 h-5 bg-white/50 dark:bg-white/10">
                    {category.count} tools
                  </Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {categories.length > 8 && (
          <div className="text-center mt-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll(!showAll)}
              className="text-muted-foreground hover:text-foreground"
            >
              {showAll ? 'Show Less' : `Show All ${categories.length} Categories`}
              <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showAll ? 'rotate-90' : ''}`} />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default CategoryBrowser;
